/**
 * Module: `export/export-styles.ts` — shared sober typography for PDF and Word.
 * PDF is the visual reference; Word mirrors these constants.
 */

/** Universal sans-serif available in Word (PDF uses Roboto via pdfmake vfs). */
export const EXPORT_SANS_FONT_DOCX = "Arial";
export const EXPORT_SANS_FONT_PDF = "Roboto";
export const EXPORT_EMOJI_FONT_PDF = "NotoEmoji";

/** Body text size in points. */
export const EXPORT_BODY_FONT_SIZE_PT = 10;

/** Heading sizes in points — matches the PDF styles the user validated. */
export const EXPORT_H1_FONT_SIZE_PT = 14;
export const EXPORT_H2_FONT_SIZE_PT = 12;
export const EXPORT_H3_FONT_SIZE_PT = 11;
export const EXPORT_CODE_FONT_SIZE_PT = 8.5;
export const EXPORT_INLINE_CODE_FONT_SIZE_PT = 9;

export const EXPORT_LINE_HEIGHT = 1.25;

/**
 * Slate-ish colors (no `#` — docx wants RRGGBB).
 * Body and H1 share the same ink as the validated PDF defaultStyle.
 */
export const EXPORT_COLOR_H1 = "0F172A";
export const EXPORT_COLOR_H2 = "1E293B";
export const EXPORT_COLOR_H3 = "334155";
export const EXPORT_COLOR_BODY = EXPORT_COLOR_H1;
export const EXPORT_COLOR_LINK = "1D4ED8";
export const EXPORT_COLOR_CODE = EXPORT_COLOR_H1;
export const EXPORT_COLOR_INLINE_CODE = EXPORT_COLOR_H3;
export const EXPORT_COLOR_CODE_BG = "F1F5F9";
export const EXPORT_COLOR_SEPARATOR = "CBD5E1";
/** Aligné sur `--secondary` (oklch 0.7044 0.153 34.44) → sRGB. */
export const EXPORT_COLOR_SECONDARY = "EF785C";

/** Paragraph / heading vertical rhythm in points (from PDF margins). */
export const EXPORT_PARAGRAPH_AFTER_PT = 8;
export const EXPORT_H1_AFTER_PT = 8;
export const EXPORT_H2_BEFORE_PT = 12;
export const EXPORT_H2_AFTER_PT = 6;
export const EXPORT_H3_BEFORE_PT = 12;
export const EXPORT_H3_AFTER_PT = 6;
export const EXPORT_LIST_AFTER_PT = 8;
export const EXPORT_SEPARATOR_MARGIN_PT = 8;
export const EXPORT_SEPARATOR_LINE_WIDTH_PT = 0.5;
/** A4 width minus the 48pt left and right export margins. */
export const EXPORT_SEPARATOR_WIDTH_PT = 499.28;

/**
 * PDF page margins in points: [left, top, right, bottom].
 * Converted to twips for Word (1pt = 20 twips).
 */
export const EXPORT_PAGE_MARGINS_PT = {
  left: 48,
  top: 56,
  right: 48,
  bottom: 56,
} as const;

export function ptToHalfPoints(pt: number): number {
  return Math.round(pt * 2);
}

export function ptToTwips(pt: number): number {
  return Math.round(pt * 20);
}

/** docx.js line spacing unit = 240ths of a line. */
export function lineHeightToDocxLine(lineHeight: number): number {
  return Math.round(240 * lineHeight);
}

export function exportColorCss(hexWithoutHash: string): string {
  return `#${hexWithoutHash.toLowerCase()}`;
}

export const EXPORT_PAGE_MARGINS_TWIPS = {
  left: ptToTwips(EXPORT_PAGE_MARGINS_PT.left),
  top: ptToTwips(EXPORT_PAGE_MARGINS_PT.top),
  right: ptToTwips(EXPORT_PAGE_MARGINS_PT.right),
  bottom: ptToTwips(EXPORT_PAGE_MARGINS_PT.bottom),
} as const;
